/* eslint-disable react/react-in-jsx-scope */
import "./polyfills";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import Configuration from "./configuration";
import { StyledEngineProvider } from "@mui/material";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { useEffect } from "react";
import { persistenceService, spiffCoreConfiguration } from "@spiffcommerce/core";
import { browserProfilingIntegration, replayIntegration } from "@sentry/browser";
import { reactRouterV6BrowserTracingIntegration } from "@sentry/react";
import ApplicationIndex from "./ApplicationIndex";

import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";

import "./i18n";
import "./styles.css";
import {
    PersistenceApp,
    PersistenceCurrency,
    PersistenceOverrideConfig,
    PersistenceOverrideTheme,
    PersistenceQuery,
    PersistenceRoute,
} from "./scenes/ProgressiveWebApp";

/**
 * We need to configure the URLs for the Spiff Core SDK to point to the correct region based on where editor is hosted.
 */

if (spiffCoreConfiguration.getHubUrl() !== Configuration.hubUrl) {
    spiffCoreConfiguration.setHubUrl(Configuration.hubUrl);
}
if (spiffCoreConfiguration.getServerUrl() !== Configuration.serverUrl) {
    spiffCoreConfiguration.setServerUrl(Configuration.serverUrl);
}
if (spiffCoreConfiguration.getServicesApiUrl() !== Configuration.servicesApiUrl) {
    spiffCoreConfiguration.setServicesApiUrl(Configuration.servicesApiUrl);
}

const enableSentry = import.meta.env.DEV !== true || Configuration.enableSentry;
if (enableSentry) {
    // Initialize Sentry to handle bug reporting and profiling of the app.
    Sentry.init({
        dsn: "https://3da97d934e864ba69ce962097a0f4930@o308186.ingest.sentry.io/1769885",
        release: Configuration.version,
        environment: import.meta.env.DEV === true ? "development" : Configuration.environment,
        attachStacktrace: true,
        debug: false,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
            browserProfilingIntegration(),
            reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        profilesSampleRate: 1,
        tracesSampleRate: 1,
        tracePropagationTargets: [
            "https://spiffcommerce.com",
            "https://au.spiffcommerce.com",
            "https://us.spiffcommerce.com",
            "https://aumelbdev.spiffcommerce.com",
            "localhost",
        ],
    });
}

/**
 * When a user attempts to install as a progressive web app we need to modify the manifest to include the current URL.
 * This is because the start_url in the manifest is used to determine the URL that the app will open to.
 */
window.addEventListener("beforeinstallprompt", () => {
    // We replace the hash as query params after the hash are not included.
    // Since we just want to parse the URL we can replace the hash with a simple /.
    const currentUrl = new URL(window.location.href.replace("/#/", "/"));
    if (!currentUrl.hash.startsWith("#/pwa")) {
        console.info("PWA configuration saved to local storage.");
        persistenceService.set(PersistenceQuery, currentUrl.searchParams.toString());
        persistenceService.set(PersistenceRoute, currentUrl.pathname.startsWith("/bundle") ? "bundle" : "transaction");
        persistenceService.set(PersistenceCurrency, currentUrl.searchParams.get("currencyCode") ?? "");
        persistenceService.set(PersistenceApp, currentUrl.searchParams.get("applicationKey") ?? "");
        persistenceService.set(PersistenceOverrideTheme, currentUrl.searchParams.get("overrideTheme") ?? "");
        persistenceService.set(PersistenceOverrideConfig, currentUrl.searchParams.get("overrideConfig") ?? "");
    }
});

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
    <StyledEngineProvider injectFirst>
        <ApplicationIndex />
    </StyledEngineProvider>,
);
